import React, { useReducer, useMemo } from "react";

import { css } from "@emotion/css";
import { captureException } from "@sentry/react";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { doctorFormattedName } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { SideSheet } from "@/uiKit/atoms/SideSheet.tsx";
import { NurseInclusionPatientCreationForm } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NurseInclusionPatientCreationForm.tsx";
import { NurseInclusionPatientCreationFormActions } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NurseInclusionPatientCreationFormActions.tsx";
import { NurseInclusionPatientCreationFormContent } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NurseInclusionPatientCreationFormContent.tsx";
import {
  isNurseInclusionWithCarePlan,
  NurseInclusionPatientCreationInfo,
} from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NurseInclusionPatientCreationInfo.ts";
import { nurseInclusionPatientCreationWizard } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclustionPatientCreationWizard.ts";
import {
  useCreateNoInsulinPatient,
  useGeneratePrescription,
  useSignPrescriptionAndCreatePatient,
} from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationAndRenewalActions.ts";
import { PatientCreationProvider } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationProvider.tsx";
import { PrescriptionSignActions } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignActions.tsx";
import { PrescriptionSignContent } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignContent.tsx";

export type NurseInclusionPatientCreationInclusionProps = {
  id: string;
  open: boolean;
  justification: string;
  onClose: () => void;
  onReject?: () => void;
  onSubmit: (patientId: string) => void;
  defaultValues: Partial<NurseInclusionPatientCreationInfo>;
  nurse: {
    firstName: string;
    lastName: string;
  };
  nurseInclusion: NurseInclusionPatient;
};

export const NurseInclusionPatientCreation: React.FC<NurseInclusionPatientCreationInclusionProps> = ({
  id,
  open,
  justification,
  nurse,
  onClose,
  onSubmit,
  defaultValues,
  nurseInclusion,
}) => {
  const { t } = useTranslation();
  const extraData = useMemo(
    () => ({
      nurseInclusion,
    }),
    [nurseInclusion],
  );

  return (
    <SideSheet id={id} open={open} onClose={onClose}>
      <CardHeader
        title={t("nurseInclusion.addPatient", {
          nurse: doctorFormattedName(nurse.lastName, nurse.firstName),
        })}
        variant="contained"
      />
      <NurseInclusionPatientCreationInclusionContent
        justification={justification}
        onBack={onClose}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        extraData={extraData}
      />
    </SideSheet>
  );
};

export type PatientCreationContentProps = {
  justification: string;
  onBack: () => void;
  onSubmit: (patientId: string) => void;
  defaultValues: Partial<NurseInclusionPatientCreationInfo>;
  extraData: {
    nurseInclusion: NurseInclusionPatient;
  };
};

const NurseInclusionPatientCreationInclusionContent: React.FC<PatientCreationContentProps> = ({
  justification,
  onBack,
  onSubmit,
  defaultValues,
  extraData,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const generatePrescription = useGeneratePrescription();
  const createNoInsulinPatient = useCreateNoInsulinPatient(
    createdPatientId => onSubmit(createdPatientId.id),
    extraData,
  );

  const [state, dispatch] = useReducer(nurseInclusionPatientCreationWizard.reducer, {
    step: "Info",
    Info: defaultValues ?? {},
  });
  const patientCreationDirectives = Queries.practitioner.usePatientCreationDirectives({
    preinclusionId: extraData.nurseInclusion.id,
  });
  const { submit: signPrescriptionAndCreatePatient, isPending: isSignAndCreatePending } =
    useSignPrescriptionAndCreatePatient(createdPatientId => onSubmit(createdPatientId.id), extraData);

  return (
    <PatientCreationProvider patientCreationDirectives={patientCreationDirectives}>
      <NurseInclusionPatientCreationForm
        className={styles.form}
        defaultValues={defaultValues}
        onSubmit={data => {
          if (data.diabetesType === "gestational" && data.insulinScheme === "noInsulin" && data.termDate) {
            createNoInsulinPatient(data, {
              diabetesType: data.diabetesType,
              insulinScheme: data.insulinScheme,
              termDate: data.termDate,
            });
          } else if (isNurseInclusionWithCarePlan(data)) {
            generatePrescription(
              {
                patientInfo: data,
                carePlan: data,
                prescriptionInfo: data,
              },
              ({ prescription }) =>
                dispatch({
                  action: "TO_PrescriptionSign",
                  data: { ...data, prescription },
                }),
            );
          } else {
            captureException(
              new Error("Unexpected form state: data matches neither noInsulin gestational nor care plan path"),
              { extra: { data } },
            );
          }
        }}
      >
        <NurseInclusionPatientCreationFormContent justification={justification} />
        <NurseInclusionPatientCreationFormActions onBack={onBack} />
      </NurseInclusionPatientCreationForm>
      <Dialog
        id="prescription-dialog"
        open={state.step === "PrescriptionSign"}
        onClose={() => dispatch({ action: "BACK_TO_Info" })}
        maxWidth="md"
        classes={{ paper: styles.dialog }}
      >
        {state.step === "PrescriptionSign" && (
          <>
            <CardHeader
              id="prescription-sign-header"
              title={t("diabetesForms.prescriptionSignature")}
              variant="contained"
              onExit={() => dispatch({ action: "BACK_TO_Info" })}
            />
            <PrescriptionSignContent prescription={state.Info.prescription} />
            <PrescriptionSignActions
              submitTitle={t("diabetesForms.signAndAddPatient")}
              onBack={() =>
                dispatch({
                  action: "BACK_TO_Info",
                })
              }
              onSubmit={() => {
                if (isNurseInclusionWithCarePlan(state.Info)) {
                  signPrescriptionAndCreatePatient(state.Info, state.Info, state.Info);
                } else {
                  captureException(new Error("Unexpected state: reached PrescriptionSign without care plan data"), {
                    extra: { info: state.Info },
                  });
                }
              }}
              pending={isSignAndCreatePending}
              className={styles.footer}
            />
          </>
        )}
      </Dialog>
    </PatientCreationProvider>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "hidden",
  }),
  dialog: css({
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    alignItems: "center",
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
  }),
});
